import type { EntityType } from './labels.js';

/**
 * Every synced field, labelled in the backoffice's own terms and typed by the backoffice's model.
 * A diff and a record view take their labels and their order from here; insertion order is display
 * order. A field missing from the registry still shows, under its name humanised.
 *
 * `perEnvironment` marks the fields that may hold a value per environment — a literal copy of each
 * server descriptor's `perEnvironment` list (the client cannot import the descriptors), kept in
 * step by `fields.test.ts`. Only those get the editor's **Per environment** control.
 */
export type FieldKind = 'text' | 'number' | 'boolean' | 'date' | 'enum' | 'numberList' | 'textList' | 'map' | 'object';
export type FieldSpec = { label: string; kind: FieldKind; perEnvironment?: true };

export const FIELDS: Record<EntityType, Record<string, FieldSpec>> = {
    studio: {
        name: { label: 'Name', kind: 'text' },
        default_launch_type: { label: 'Default launch type', kind: 'enum' },
        default_bet_values_eur: { label: 'Default bet values (EUR)', kind: 'numberList' },
        analytics_conf: { label: 'Analytics config', kind: 'object', perEnvironment: true },
        client_template: { label: 'Client template', kind: 'text' },
        error_template: { label: 'Error template', kind: 'text' },
        hide_libs: { label: 'Hide libs', kind: 'boolean' },
        cdn_url: { label: 'CDN URL', kind: 'text', perEnvironment: true },
        lib_cms_url: { label: 'CMS URL', kind: 'text', perEnvironment: true },
        lib_ui_url: { label: 'UI URL', kind: 'text', perEnvironment: true },
        lib_bridge_url: { label: 'Bridge URL', kind: 'text', perEnvironment: true },
    },
    game: {
        display_name: { label: 'Name', kind: 'text' },
        engine_id: { label: 'Engine', kind: 'text' },
        engine_conf: { label: 'Engine config', kind: 'object' },
        bet_multiplier: { label: 'Bet multiplier', kind: 'number' },
        additional_bet_multipliers: { label: 'Additional bet multipliers', kind: 'numberList' },
        bet_values: { label: 'Bet values', kind: 'numberList' },
        rtps: { label: 'RTPs', kind: 'map' },
        autoplay_options: { label: 'Autoplay options', kind: 'numberList' },
        launch_type: { label: 'Launch type', kind: 'enum' },
        is_variable_bet_size: { label: 'Variable bet size', kind: 'boolean' },
        description: { label: 'Description', kind: 'text' },
        features: { label: 'Features', kind: 'textList' },
        thumbnail_key: { label: 'Thumbnail', kind: 'text' },
        cover_image_key: { label: 'Cover image', kind: 'text' },
        pre_release_date: { label: 'Pre-release date', kind: 'date' },
        release_date: { label: 'Release date', kind: 'date' },
        lib_client_url: { label: 'Client URL', kind: 'text', perEnvironment: true },
        lib_cms_url: { label: 'CMS URL', kind: 'text', perEnvironment: true },
        lib_ui_url: { label: 'UI URL', kind: 'text', perEnvironment: true },
        lib_bridge_url: { label: 'Bridge URL', kind: 'text', perEnvironment: true },
        status: { label: 'Status', kind: 'enum', perEnvironment: true },
    },
    config: {
        value: { label: 'Value', kind: 'text', perEnvironment: true },
        description: { label: 'Description', kind: 'text' },
    },
};

/** `default_launch_type` → `Default launch type`. */
export const humanise = (field: string): string => {
    const words = field
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .split(/[\s_-]+/)
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : field;
};

export const fieldSpec = (type: EntityType, field: string): FieldSpec =>
    FIELDS[type]?.[field] ?? { label: humanise(field), kind: 'text' };

/** A field's place in display order; fields missing from the registry sort last. */
export const fieldOrder = (type: EntityType, field: string): number => {
    const index = Object.keys(FIELDS[type] ?? {}).indexOf(field);
    return index < 0 ? Number.MAX_SAFE_INTEGER : index;
};

/** Whether a field may hold a value per environment (the server descriptor's `perEnvironment`). */
export const isPerEnvironment = (type: EntityType, field: string): boolean =>
    FIELDS[type]?.[field]?.perEnvironment === true;

/** Of the fields that differ in one environment, those it can keep as its own: the per-environment ones. */
export const adoptableFields = (type: EntityType, differing: readonly string[]): string[] => [
    ...new Set(differing.filter((field) => isPerEnvironment(type, field))),
];
